  import axios from 'axios'
import { EventBus } from './event-bus.js'

const baseUrl = '/padmin/api'
export const sensitiveFields = require('../sensitive_fields.json')

function getAuthHeaders () {
  let token = window.localStorage['auth_token']
  if (!token) {
    return { 'headers': {} }
  }
  return { 'headers': { 'Authorization': 'Bearer ' + token } }
}

export function get (url) {
  return new Promise(function (resolve, reject) {
    axios.get(baseUrl + url, getAuthHeaders()).then(resp => {
      resolve(resp)
    }, err => {
      console.log('reject', err)
      reject(err)
    })
  })
}

export function forceFileDownload(data, fileName, options={}) {
  const url = window.URL.createObjectURL(new Blob([data], options))
  const link = document.createElement("a")
  link.href = url
  link.setAttribute("download", fileName)
  document.body.appendChild(link)
  link.click()
}

export function getBinary (url, onDownloadProgress) {
  return new Promise(function (resolve, reject) {
    let config = getAuthHeaders()
    config['responseType'] = 'arraybuffer'
    if (onDownloadProgress) {
      config['onDownloadProgress'] = onDownloadProgress
    }
    axios.get(baseUrl + url, config).then(resp => {
      resolve(resp)
    }, err => {
      console.log('reject', err)
      reject(err)
    })
  })
}

async function getEncParams() {
  let encoder = null
  let publicKey = null
  let spqr = localStorage.getItem('spqr')
  if (!spqr) {
    return null
  }
  let s2 = null
  if(spqr){
    let s1 = spqr.slice(0, 32)
    s2 = spqr.slice(32, 64)
    encoder = new TextEncoder()
    publicKey = await window.crypto.subtle.importKey(
      "raw",
      str2ab(s1),
      "AES-GCM",
      true,
      ["encrypt", "decrypt"]
    )
  }

  return {'encoder': encoder, 'publicKey': publicKey, 's2': s2}
}

function str2ab(str) {
  const buf = new ArrayBuffer(str.length);
  const bufView = new Uint8Array(buf);
  for (let i = 0, strLen = str.length; i < strLen; i++) {
      bufView[i] = str.charCodeAt(i);
  }
  return buf;
}

function arrayBufferToHex(buf) {
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, "0")).join("");
}

async function uglifyVal(name, val, encParams){
  if (!val) {
    return val
  }
  if (typeof val == 'object') {
    for (let [nName, nVal] of Object.entries(val)) {
      let nUglifiedVal = await uglifyVal(nName, nVal, encParams)
      if (nUglifiedVal != nVal) {
        val[nName] = nUglifiedVal
      }
    }
    return val
  }

  if (!sensitiveFields.includes(name)) {
    return val
  }

  const encoded = encParams.encoder.encode(val)
  const em = await window.crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv: str2ab(encParams.s2)
    },
    encParams.publicKey,
    encoded
  )
  const result = arrayBufferToHex(em)
  return result
}

export function post (url, data, extraHeaders, onUploadProgress) {
  return new Promise(function (resolve, reject) {
    let config = getAuthHeaders()
    for (let extraHeaderName in extraHeaders) {
      config['headers'][extraHeaderName] = extraHeaders[extraHeaderName]
    }
    if (onUploadProgress) {
      config['onUploadProgress'] = onUploadProgress
    }
    axios.post(baseUrl + url, data, config).then((resp) => {
      resolve(resp)
    }, err => {
      reject(err)
    })
  })
}

export function del (url) {
  return new Promise(function (resolve, reject) {
    axios.delete(baseUrl + url, getAuthHeaders()).then(resp => {
      resolve(resp)
    }, err => {
      console.log('reject', err)
      reject(err)
    })
  })
}

export function patch (url, data, extraHeaders) {
  return new Promise(function (resolve, reject) {
    let config = getAuthHeaders()
    for (let extraHeaderName in extraHeaders) {
      config['headers'][extraHeaderName] = extraHeaders[extraHeaderName]
    }

    axios.patch(baseUrl + url, data, config).then((resp) => {
      resolve(resp)
    }, err => {
      reject(err)
    })
  })
}

export async function getFreeDiskSpace() {
  const resp = await get('/free_disk_space')
  return resp.data
}

export async function uploadStart(scriptName) {
  var resp  = await get('/upload_start?script_name=' + scriptName)
  return resp ? resp.data : null
}

axios.interceptors.response.use(function (response) {
  return response
}, function (error) {
  if (error.response) {
    if (error.response.status === 401) {
      EventBus.$emit('logged out')
      return
    } else if (error.response.status === 403) {
      EventBus.$emit('forbidden')
      return
    } else if (error.response.status == 409) {
      EventBus.$emit('conflict', error.response.data)
    }
  }
  return Promise.reject(error)
})

axios.interceptors.request.use(async request => {
  let encParams = await getEncParams()
  if (!encParams) {
    return request
  }
  if (request.data) {
    if(request.data instanceof FormData) {
      for (let [name, val] of request.data.entries()) {
        let uglifiedVal = await uglifyVal(name, val, encParams)
        if (uglifiedVal != val) {
          request.data.set(name, uglifiedVal)
        }
      }
    } else {
      for (let [name, val] of Object.entries(request.data)) {
        let uglifiedVal = await uglifyVal(name, val, encParams)
        if (uglifiedVal != val) {
          request.data[name] = uglifiedVal
        }
      }
    }
  }
  return request
}, function (error) {
    return Promise.reject(error);
})
